/**
 * Settings sections — Quick 261013-srg PR 1 (the shell), PR 2 (role gates).
 *
 * One Settings area with a left section list. A role sees a section only when every API call
 * the section makes will succeed for it, so no visible control ends in a 403. Each section names
 * the permissions those calls need (`apiPermissions`); the test checks, for every role, that
 * `visible` agrees with them exactly. The gates themselves live in lib/role-gates.ts.
 *
 * Pure data + pure functions, so routing and per-role visibility are unit-testable.
 */

import {
  API_CONNECTIONS_VIEW_ROLES,
  IMPORT_ROLES,
  MANAGE_ROLES,
  MANAGE_USERS_ROLES,
  MANAGE_VALIDATION_RULES_ROLES,
  REVIEW_RULES_VIEW_ROLES,
  SETTINGS_ADMIN_ROLES,
} from '../../lib/role-gates';

export type SettingsSectionId =
  | 'getting-started'
  | 'account'
  | 'review'
  | 'driver-rules'
  | 'drivers-shifts'
  | 'jobs-pricing'
  | 'company-logo'
  | 'printed-tickets'
  | 'customer-emails'
  | 'disposal-sharing'
  | 'reporting'
  | 'accounting'
  | 'team'
  | 'api-connections'
  | 'import-data';

export interface SettingsSection {
  readonly id: SettingsSectionId;
  readonly label: string;
  /** Who sees the section in the menu and may open its URL (a UX gate; the API is authoritative). */
  readonly gate: ReadonlySet<string> | 'everyone';
  /**
   * The API permissions the section's calls need for the role that sees it. For Ticket review
   * this is the READ set: managers view it read-only, and the save controls are gated on
   * SETTINGS_ADMIN_ROLES (MANAGE_TENANT) inside the panels.
   */
  readonly apiPermissions: readonly string[];
}

/**
 * Menu order, from the plan's target structure. "Company profile" is not its own section yet:
 * company info is still step 1 of Getting started until PR 4. `import-data`, not `import`,
 * because `/settings/import` is the full-page import tool.
 */
export const SETTINGS_SECTIONS: readonly SettingsSection[] = [
  {
    id: 'getting-started',
    label: 'Getting started',
    gate: MANAGE_ROLES,
    // setup-wizard-status, company-info, job types writes.
    apiPermissions: ['MANAGE_REFERENCE_DATA'],
  },
  { id: 'account', label: 'My account', gate: 'everyone', apiPermissions: [] },
  {
    id: 'review',
    label: 'Ticket review & approvals',
    gate: REVIEW_RULES_VIEW_ROLES,
    // GET inreview-settings (MANAGE_REFERENCE_DATA), GET completion-policy (VIEW_RUN_TICKETS).
    apiPermissions: ['MANAGE_REFERENCE_DATA', 'VIEW_RUN_TICKETS'],
  },
  {
    // OPEN-217 (DEC-382) — admin and billing staff, never dispatch. The editor reads job types
    // from /v1/validation-rules itself (billing roles can't read /v1/job-types).
    id: 'driver-rules',
    label: 'Driver entry rules',
    gate: MANAGE_VALIDATION_RULES_ROLES,
    // /v1/validation-rules (MANAGE_VALIDATION_RULES); the Extra Services list for
    // extra-service rules (VIEW_SERVICE_CHARGES).
    apiPermissions: ['MANAGE_VALIDATION_RULES', 'VIEW_SERVICE_CHARGES'],
  },
  {
    id: 'drivers-shifts',
    label: 'Drivers & shifts',
    gate: SETTINGS_ADMIN_ROLES,
    // shift-settings (MANAGE_TENANT); tenants/settings for Arrivals (VIEW_RUN_TICKETS; its
    // PATCH is EDIT_GEOFENCE, gated inside the panel, OPEN-220a).
    apiPermissions: ['MANAGE_TENANT', 'VIEW_RUN_TICKETS'],
  },
  {
    id: 'jobs-pricing',
    label: 'Jobs, products & pricing',
    gate: MANAGE_ROLES,
    // Products. Extra Services, Running costs and Site types are gated per editor inside.
    apiPermissions: ['MANAGE_REFERENCE_DATA', 'VIEW_RUN_TICKETS'],
  },
  {
    id: 'company-logo',
    label: 'Company logo',
    gate: SETTINGS_ADMIN_ROLES,
    // company-logo PUT/DELETE (MANAGE_TENANT); company-logo + branding GET (VIEW_RUN_TICKETS).
    apiPermissions: ['MANAGE_TENANT', 'VIEW_RUN_TICKETS'],
  },
  {
    id: 'printed-tickets',
    label: 'Printed tickets',
    gate: SETTINGS_ADMIN_ROLES,
    apiPermissions: ['MANAGE_TENANT', 'VIEW_RUN_TICKETS'], // print-config + logo
  },
  {
    id: 'customer-emails',
    label: 'Customer emails',
    gate: SETTINGS_ADMIN_ROLES,
    apiPermissions: ['MANAGE_TENANT'], // tenants/notification-settings
  },
  {
    id: 'disposal-sharing',
    label: 'Disposal sites',
    gate: SETTINGS_ADMIN_ROLES,
    apiPermissions: ['MANAGE_TENANT'], // disposal-facility-disclosures GET + PUT (16.8)
  },
  {
    id: 'reporting',
    label: 'Reporting rules',
    gate: SETTINGS_ADMIN_ROLES,
    apiPermissions: ['MANAGE_TENANT'], // on-time-settings + tank-estimates-settings (13.1c)
  },
  {
    id: 'accounting',
    label: 'Accounting export',
    gate: SETTINGS_ADMIN_ROLES,
    // accounting-export-settings (MANAGE_TENANT) + the download (VIEW_INVOICES); bill-date-settings
    // GET (VIEW_RUN_TICKETS) + PATCH (MANAGE_TENANT), gap 6.
    apiPermissions: ['MANAGE_TENANT', 'VIEW_INVOICES', 'VIEW_RUN_TICKETS'],
  },
  {
    id: 'team',
    label: 'Team & access',
    gate: MANAGE_USERS_ROLES,
    apiPermissions: ['MANAGE_USERS'], // /v1/users
  },
  {
    id: 'api-connections',
    label: 'API connections',
    gate: API_CONNECTIONS_VIEW_ROLES,
    // GET /v1/operator-api-credentials, read-only (operator portal rework PR 9).
    apiPermissions: ['VIEW_OPERATOR_API_CREDENTIALS'],
  },
  {
    id: 'import-data',
    label: 'Import data',
    gate: IMPORT_ROLES,
    apiPermissions: ['MANAGE_HAULER_CATALOG'],
  },
];

export function canSeeSettingsSection(section: SettingsSection, role: string): boolean {
  return section.gate === 'everyone' || section.gate.has(role);
}

/** The sections a role sees, in menu order. */
export function visibleSettingsSections(role: string): readonly SettingsSection[] {
  return SETTINGS_SECTIONS.filter((s) => canSeeSettingsSection(s, role));
}

/** Where `/settings` lands: Getting started for managers and admins, My account for everyone else. */
export function defaultSettingsSection(role: string): SettingsSectionId {
  return MANAGE_ROLES.has(role) ? 'getting-started' : 'account';
}
